import { createSlice } from '@reduxjs/toolkit'
import {
  IParticipant,
  CodeRequest,
  TokenRequest,
  IChild,
  IParticipantVerificationData
} from './interfaces'
import { AppThunk } from '../rootReducer'
import { ParticipantCodeFormType } from '../../components/participant/ParticipantCodeForm'
import axios from 'axios'
import { addToast, setLoadedViaCode, setLoadedViaFamilyToken } from '../misc/misc'
import {
  addTokenRegistration,
  fixRegistrationPriorities,
} from '../registrations/registrations'
import { setAnsweredParticipantCustomQuestions } from '../program/program'
import { ToastType } from '../misc/interfaces'
import qs from 'qs'
import Moment from 'moment'
import * as routes from '../../routes'
import { IProgram } from '../program/interfaces'
import { CustomAnswer, IEvent } from '../events/interfaces'
import {
  PartialDebitData,
  setPaymentMethod, updateDebitData,
} from '../paymentData'
import { Dispatch, SetStateAction } from "react"
import { NavigateFunction } from 'react-router-dom'
import { checkEventRegistrationDenied } from '../../components/shared/RegisterButton'
import { AddTokenRegistrationPayload } from '../registrations/interfaces'

export const initialState = {
  data: {
    ...({} as IParticipant),
    originalRegistrationIds: [] as number[], // or string[] if the IDs are strings
    originalCustomAnswers: [] as { eventId: number, questionId: number }[],
  },
  loaded: false,
  participantFromBackend: {} as IParticipantVerificationData | undefined
}

const participantSlice = createSlice({
  name: 'participant',
  initialState,
  reducers: {
    setParticipant(state, action) {
      state.data = action.payload
      if(!action.payload.originalRegistrationIds) {
        state.data.originalRegistrationIds = []
      }
      if(action.payload.saveAsParticipantFromBackend) {
        state.participantFromBackend = Object.assign({}, action.payload)
      }
    },
    setParticipantBirthday(state, action) {
      state.data.date_of_birth = action.payload
    },
    setParticipantAccessKey(state, action) {
      state.data.access_key = action.payload
    },
    addTokenOriginalRegistrationID(state, action) {
      state.data.originalRegistrationIds.push(action.payload)
    },
    setOriginalCustomAnswers(state, action) {
      state.data.originalCustomAnswers = action.payload
    },
    setParticipantChildren(state, action) {
      state.data.children = action.payload
    },
    removeParticipantChild(state, action) {
      state.data.children = state.data.children.filter((child: IChild) => {
        return child.id !== action.payload.id
      })
    },
    setDisability(state, action) {
      state.data.disabled = action.payload
    },
  },
})

export default participantSlice.reducer
export const {
  setParticipant,
  setParticipantAccessKey,
  addTokenOriginalRegistrationID,
  setOriginalCustomAnswers,
  setParticipantChildren,
} = participantSlice.actions

export const extractTokenFromFamilyToken = (familyToken: string) => {
  return familyToken.split('family-')[1]
}

interface AddressParts {
  streetAndHouse: string
  zip: string
  city: string
}

const extractAddressParts = (addressString: string): AddressParts | null => {
  const regex = /^(.+?)\s+(\d{5})\s+(.+)$/;
  const match = addressString.match(regex)

  if (match && match[1] && match[2] && match[3]) { // Ensure all three capturing groups are found
    return {
      streetAndHouse: match[1].trim(),
      zip: match[2].trim(),            // Second capturing group (ZIP code)
      city: match[3].trim()             // Third capturing group (City)
    }
  } else {
    return null // Return null if the address string doesn't match the expected format
  }
}

export const setParticipantFromFamilyToken = (data: IParticipantVerificationData, navigate: NavigateFunction): AppThunk => async (dispatch, getState) => {
  data.accesskeys = []
  data.disabled = false
  data.date_of_birth = ""
  data.first_name = ""
  data.token = ""
  data.gender = ""

  // family_token is already in data!

  dispatch(setParticipant(data))
  dispatch(setLoadedViaFamilyToken(true))

  const program = getState().program.data
  const events = getState().events.list
  let idx = 1
  for(const reg of data.registrations) {
    const event = events.find(
      (event: IEvent) => event.event_id === reg.event_id
    )
    if (event && !checkEventRegistrationDenied(program, event)) {
      const registrationPrice = reg.price_category?.price || event.price || 0
      const newReg = {
        event_id: event.event_id,
        event_name: event.name,
        event_rid: event.relative_id,
        event: event,
        priority: idx++,
        price: registrationPrice,
        price_category: reg.price_category,
        group_code: reg.group_code,
        custom_answers:  reg.custom_answers,
        special_notes: reg.special_notes,
        number_of_adults: reg.number_of_adults,
        number_of_children: reg.number_of_children,
        loadedFromBackend: false
      } as AddTokenRegistrationPayload

      dispatch(addTokenRegistration(newReg))
    } else {
      console.warn(`Did not create registration for ${reg.event_name}`, reg)
    }
  }

  const paymentCodes: Record<string, string> = {
    '2_CASH': 'cash',
    '4_DEBIT': 'debit',
    '3_TRANSFER': 'transfer',
    '5_ONLINE': 'online',
    '6_INVOICE': 'invoice',
    '1_CUSTOM': 'custom',
  }
  dispatch(setPaymentMethod(paymentCodes[data.basic_fee_type ?? ""]))

  const debitData: PartialDebitData = {
    iban: data.account_iban,
    bic: data.account_bic,
    payer_name: data.account_payer_name,
    payer_is_holder: !data.account_holder_name,
    holder_name: data.account_holder_name,
    acknowledged: false,
  }

  if(data.account_payer_address) {
    const parts = extractAddressParts(data.account_payer_address)
    if (parts) {
      debitData.payer_zip = parts.zip
      debitData.payer_city = parts.city
      debitData.payer_street = parts.streetAndHouse
    } else {
      console.log(`Could not parse: ${data.account_payer_address}`)
    }
  }

  if(data.account_holder_address) {
    const parts = extractAddressParts(data.account_holder_address)
    if (parts) {
      debitData.holder_zip = parts.zip
      debitData.holder_city = parts.city
      debitData.holder_street = parts.streetAndHouse
    } else {
      console.log(`Could not parse: ${data.account_payer_address}`)
    }
  }

  dispatch(updateDebitData(debitData))

  // This triggers a soft page reload, so that the loaded data is in the form without some special behaviour
  navigate('/empty', { replace: true })
  navigate(routes.EVENTS.path, { replace: true })
}

export const setParticipantFromApi = (response_data: any, navigate: NavigateFunction, via: 'code' | 'token', code: string): AppThunk => async (dispatch, getState) => {
  console.log(`Setting data from ${via}`)
  const events = getState().events.list
  const registrations = response_data.registrations

  // family_token handling
  const is_family_token = code.startsWith('family-')
  if(is_family_token) {
    code = extractTokenFromFamilyToken(code)
  }

  delete response_data.registrations
  response_data.date_of_birth = Moment(
    response_data.date_of_birth,
    'YYYY-MM-DD'
  ).format('DD.MM.YYYY')

  // set handicap from disabled
  if (response_data.disabled && response_data.disabled !== "") {
    response_data.handicap = response_data.disabled
    response_data.disabled = true
  } else {
    response_data.disabled = false
    response_data.handicap = ''
  }

  if(response_data.accesskeys.length > 0) {
    response_data.access_key = response_data.accesskeys[0].code
  }

  dispatch(setParticipant({ ...response_data, saveAsParticipantFromBackend: true }))
  if(via === 'code') {
    dispatch(setParticipantAccessKey(code))
  }

  registrations.forEach((reg: any) => {
    reg.event = events.find(
      (event: IEvent) => event.event_id === reg.event_id
    )
    reg.loadedFromBackend = true
    dispatch(addTokenRegistration(reg))
    dispatch(addTokenOriginalRegistrationID(reg.event_id))
  })
  dispatch(fixRegistrationPriorities({}))
  const paymentCodes: Record<string, string> = {
    '2_CASH': 'cash',
    '4_DEBIT': 'debit',
    '3_TRANSFER': 'transfer',
    '5_ONLINE': 'online',
    '6_INVOICE': 'invoice',
    '1_CUSTOM': 'custom',
  }
  dispatch(setPaymentMethod(paymentCodes[response_data.basic_fee_type]))
  dispatch(updateDebitData({
    iban: response_data.account_iban,
    bic: response_data.account_bic,
    payer_name: response_data.account_payer_name,
    payer_address: response_data.account_payer_address,
    payer_is_holder: !response_data.account_holder_name,
    holder_address: response_data.account_holder_address,
    holder_name: response_data.account_holder_name,
    acknowledged: true,
  }))
  dispatch(setLoadedViaCode(true))

  const answeredQuestions: any[] = []
  const originalCustomAnswers: { eventId: number, questionId: number }[] = []

  getState().program.data.participant_questions.forEach((answer: any) => {
    response_data.custom_answers.forEach((givenAnswer: CustomAnswer) => {
      if (givenAnswer.question_id === answer.id && givenAnswer.answer_text) {
        answeredQuestions.push(answer.id)
      }
    })
  })

  // Track custom questions that were already answered for each registration
  registrations.forEach((reg: any) => {
    console.log("Checking if given registration already had custom question answers", reg)
    const event = events.find((event: IEvent) => event.event_id === reg.event_id)
    if (event?.registration_custom_fields) {
      event.registration_custom_fields.forEach((question: any) => {
        reg.custom_answers.forEach((givenAnswer: CustomAnswer) => {
          if (givenAnswer.question_id === question.id && givenAnswer.answer_text) {
            console.log("Recording custom answer", reg.event_id, question)
            originalCustomAnswers.push({ eventId: reg.event_id, questionId: question.id })
          }
        })
      })
    }
  })

  dispatch(setAnsweredParticipantCustomQuestions(answeredQuestions))
  dispatch(setOriginalCustomAnswers(originalCustomAnswers))
  dispatch(
    addToast({
      type: ToastType.Success,
      title: 'Daten erfolgreich geladen',
      body:
        'Bitte beachten Sie das bereits exisiterende' +
        ' Daten zwar angezeigt werden (Anmeldungen, persönliche Daten usw.) aber nicht angepasst werden können.',
    })
  )



  // This triggers a soft page reload, so that the loaded data is in the form without some special behaviour
  navigate('/empty', { replace: true })
  if(via === 'token') {
    navigate(routes.EVENTS.path, { replace: true })
  } else {
    navigate(routes.PARTICIPANT.path, { replace: true })
  }

}

export const setParticipantViaCode = (
  data: ParticipantCodeFormType,
  program: IProgram,
  setShow: Dispatch<SetStateAction<boolean>>,
  navigate: NavigateFunction
): AppThunk => async (dispatch, getState) => {
  const { baseUrl, programId } = getState().misc
  const birthday: string = Moment(data.birthday, 'DD.MM.YYYY').format(
    'YYYY-MM-DD'
  )
  const requestData: CodeRequest = {
    program: programId,
    access_key: data.code,
    date_of_birth: birthday,
    email: data.email,
  }
  try {
    const response = await axios({
      method: 'POST',
      url: `${baseUrl}/api/custom/verify/account/`,
      data: qs.stringify(requestData),
    })
    const response_data = response.data[0]
    dispatch(setParticipantFromApi(response_data, navigate, 'code', data.code))
  } catch (error: unknown) {
    console.error(error)
    dispatch(
      addToast({
        type: ToastType.Danger,
        title: 'Fehler',
        body: 'Teilnehmer konnte nicht gefunden werden.',
      })
    )
  }
}


export const setParticipantViaToken = (
  token: string,
  navigate: NavigateFunction
): AppThunk => async (dispatch, getState) => {
  const { baseUrl, programId } = getState().misc
  const isFamilyToken = token.startsWith('family-')

  const requestData: TokenRequest = {
    program: programId,
    token: isFamilyToken ? token.split('family-')[1] : token,
    token_name: isFamilyToken ? "family_token" : "token",
  }
  try {
    const response = await fetch(`${baseUrl}/api/custom/verify/account/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: qs.stringify(requestData),
      redirect: 'manual',
      //@ts-ignore
      credentials: window._is_react_dev_server ? "include" : "same-origin",
    });

    const responseJson = await response.json();
    if (!response.ok) {
      if (response.status === 401) {
        if ("url" in responseJson) {
          //@ts-ignore
          if (window._is_react_dev_server) {
            window.location.href = `http://localhost:8000${responseJson.url}`
          } else {
            window.location.href = responseJson.url
          }
        }
      } else {
        console.error(`Error requesting token data: ${response.statusText}`)
      }
    }

    const participant_data = responseJson[0];

    if(isFamilyToken) {
      dispatch(setParticipantFromFamilyToken(participant_data, navigate))
    } else {
      dispatch(setParticipantFromApi(participant_data, navigate, 'token', token))
    }
  } catch (error: unknown) {
    console.error(error)
    dispatch(
      addToast({
        type: ToastType.Danger,
        title: 'Fehler',
        body: 'Teilnehmer konnte nicht gefunden werden.',
      })
    )
  }
}


export const setCityViaZip = (
  zipCode: string,
  callback: Function
): AppThunk => async (dispatch, getState) => {
  const { baseUrl } = getState().misc
  if (zipCode && zipCode.length === 5) {
    try {
      const response = await axios({
        method: 'GET',
        url: `${baseUrl}/api/custom/validate/zipcode/?zip_code=${zipCode}`,
      })
      callback(response.data)
    } catch (error: unknown) {
      console.error(error)
    }
  }
}
